"use client";

import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Share2 } from "lucide-react";

interface ShareButtonsProps {
  /** Full URL to share (with query params) */
  shareUrl: string;
  /** Optional title for native share (e.g. "Compound Interest Calculator") */
  title?: string;
  /** Optional className for the wrapper */
  className?: string;
}

/**
 * Share button: uses native share on mobile when available, falls back to copy.
 */
export function ShareButtons({ shareUrl, title, className }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(async () => {
    if (!shareUrl) return;

    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: title ?? "CalcReturn",
          url: shareUrl,
          text: title ?? "Check out this calculator",
        });
        return;
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
      }
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }, [shareUrl, title]);

  return (
    <div className={`flex gap-2 ${className ?? ""}`}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleShare}
        className="text-muted-foreground"
        aria-label={copied ? "Copied to clipboard" : "Share"}
      >
        <Share2 className="size-3.5" aria-hidden />
        {copied ? "Copied!" : "Share"}
      </Button>
    </div>
  );
}
